import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import PrivacySettings from '@/components/PrivacySettings';
import HandleSettings from '@/components/HandleSettings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LogOut, Save, Shield, ShieldCheck, ShieldOff, Copy, Eye, EyeOff, Sun, Moon, BadgeCheck, Clock, XCircle, CheckCircle2, Building2, User, FileText, AlertCircle, Download, Trash2, Database, Lock, AtSign, Leaf, Coffee, Play, Pause, History, TrendingUp, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = ({ user }) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
    avatar: user?.avatar || '',
  });
  const [saving, setSaving] = useState(false);
  
  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [loading2FA, setLoading2FA] = useState(false);

  // Verification State
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [verificationType, setVerificationType] = useState('individual');
  const [verificationForm, setVerificationForm] = useState({
    full_name: user?.name || '',
    id_type: 'passport',
    company_name: '',
    company_role: '',
    company_website: '',
    company_email: '',
    additional_info: '',
    document_urls: []
  });
  const [submittingVerification, setSubmittingVerification] = useState(false);

  // Digital Detox State
  const [detoxStatus, setDetoxStatus] = useState(null);
  const [detoxPresets, setDetoxPresets] = useState([]);
  const [detoxStats, setDetoxStats] = useState(null);
  const [showDetoxSetup, setShowDetoxSetup] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('24_hours');
  const [detoxReason, setDetoxReason] = useState('');
  const [autoReply, setAutoReply] = useState("I'm taking a break from social media. I'll respond when I'm back! 🌿");
  const [hideFromSearch, setHideFromSearch] = useState(false);
  const [loadingDetox, setLoadingDetox] = useState(false);

  useEffect(() => {
    fetch2FAStatus();
    fetchVerificationStatus();
    fetchDetoxStatus();
    fetchDetoxPresets();
    fetchDetoxStats();
  }, []);

  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/security/2fa/status`, { withCredentials: true });
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status');
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/verification/status`, { withCredentials: true });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch verification status');
    }
  };

  const fetchDetoxStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/detox/status`, { withCredentials: true });
      setDetoxStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch detox status');
    }
  };

  const fetchDetoxPresets = async () => {
    try {
      const response = await axios.get(`${API_URL}/detox/presets`, { withCredentials: true });
      setDetoxPresets(response.data.presets || []);
    } catch (error) {
      console.error('Failed to fetch detox presets');
    }
  };

  const fetchDetoxStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/detox/stats`, { withCredentials: true });
      setDetoxStats(response.data);
    } catch (error) {
      console.error('Failed to fetch detox stats');
    }
  };

  const startDetox = async () => {
    setLoadingDetox(true);
    try {
      const response = await axios.post(
        `${API_URL}/detox/start`,
        {
          duration_preset: selectedPreset,
          reason: detoxReason || null,
          hide_from_search: hideFromSearch,
          auto_reply_message: autoReply
        },
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Digital detox started! 🌿');
      setShowDetoxSetup(false);
      fetchDetoxStatus();
      fetchDetoxStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start detox');
    } finally {
      setLoadingDetox(false);
    }
  };

  const endDetox = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to end your detox early?\n\nYou were doing great! Consider waiting a bit longer. 🌿'
    );
    if (!confirmed) return;
    
    setLoadingDetox(true);
    try {
      const response = await axios.post(
        `${API_URL}/detox/end`,
        { confirm: true, reason: 'user_ended' },
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Welcome back! 🌟');
      fetchDetoxStatus();
      fetchDetoxStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end detox');
    } finally {
      setLoadingDetox(false);
    }
  };

  const submitVerificationRequest = async () => {
    if (!verificationForm.full_name) {
      toast.error('Please enter your full name');
      return;
    }
    
    if (verificationType === 'company' && !verificationForm.company_name) {
      toast.error('Please enter your company name');
      return;
    }

    setSubmittingVerification(true);
    try {
      await axios.post(
        `${API_URL}/verification/request`,
        {
          request_type: verificationType,
          full_name: verificationForm.full_name,
          id_type: verificationType === 'individual' ? verificationForm.id_type : null,
          company_name: verificationType === 'company' ? verificationForm.company_name : null,
          company_role: verificationType === 'company' ? verificationForm.company_role : null,
          company_website: verificationType === 'company' ? verificationForm.company_website : null,
          company_email: verificationType === 'company' ? verificationForm.company_email : null,
          additional_info: verificationForm.additional_info,
          document_urls: verificationForm.document_urls
        },
        { withCredentials: true }
      );
      toast.success('Verification request submitted successfully!');
      setShowVerificationForm(false);
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification request');
    } finally {
      setSubmittingVerification(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/users/profile`,
        formData,
        { withCredentials: true }
      );
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleEnable2FA = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    setLoading2FA(true);
    try {
      const response = await axios.post(
        `${API_URL}/security/2fa/enable`,
        { password },
        { withCredentials: true }
      );
      setTwoFASetupData(response.data);
      setShow2FASetup(true);
      toast.success('Scan the QR code with your authenticator app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable 2FA');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode) {
      toast.error('Please enter verification code');
      return;
    }
    setLoading2FA(true);
    try {
      await axios.post(
        `${API_URL}/security/2fa/verify`,
        { code: verifyCode },
        { withCredentials: true }
      );
      toast.success('2FA enabled successfully!');
      setShow2FASetup(false);
      setTwoFASetupData(null);
      setPassword('');
      setVerifyCode('');
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || !disablePassword) {
      toast.error('Please enter your password and 2FA code');
      return;
    }
    setLoading2FA(true);
    try {
      await axios.post(
        `${API_URL}/security/2fa/disable`,
        { code: disableCode, password: disablePassword },
        { withCredentials: true }
      );
      toast.success('2FA disabled successfully');
      setDisableCode('');
      setDisablePassword('');
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setLoading2FA(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 pb-32" data-testid="settings-page">
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-heading font-black text-gray-900 mb-2" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-lg text-gray-600">Manage your account and preferences</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md mb-6">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Profile Information</h2>
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="font-semibold text-gray-700 mb-2 block">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl border-2 h-12"
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <Label htmlFor="username" className="font-semibold text-gray-700 mb-2 block">Username</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="rounded-xl border-2 h-12"
                  data-testid="settings-username-input"
                />
              </div>
              <div>
                <Label htmlFor="bio" className="font-semibold text-gray-700 mb-2 block">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="rounded-xl border-2 resize-none"
                  rows={3}
                  placeholder="Tell us about yourself..."
                  data-testid="settings-bio-input"
                />
              </div>
              <div>
                <Label htmlFor="avatar" className="font-semibold text-gray-700 mb-2 block">Avatar URL</Label>
                <Input
                  id="avatar"
                  value={formData.avatar}
                  onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                  className="rounded-xl border-2 h-12"
                  placeholder="https://example.com/avatar.jpg"
                  data-testid="settings-avatar-input"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform"
                data-testid="settings-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>

          {/* Custom Handle Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <AtSign className="w-7 h-7 text-primary" />
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Custom Handle</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Premium & Verified users can customize their @handle</p>
              </div>
            </div>
            <HandleSettings user={user} />
          </Card>

          {/* Security / 2FA Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-7 h-7 text-primary" />
              <h2 className="text-2xl font-heading font-bold text-gray-900">Security</h2>
            </div>
            
            <div className="space-y-4">
              {/* 2FA Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  {twoFAStatus.enabled ? (
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                  ) : (
                    <ShieldOff className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-600">
                      {twoFAStatus.enabled 
                        ? `Enabled • ${twoFAStatus.backup_codes_remaining} backup codes remaining`
                        : 'Add an extra layer of security to your account'
                      }
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  twoFAStatus.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {twoFAStatus.enabled ? 'Active' : 'Off'}
                </span>
              </div>

              {!twoFAStatus.enabled && !show2FASetup && (
                <div className="space-y-3">
                  <Label className="font-semibold text-gray-700">Enter password to enable 2FA</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your current password"
                    className="rounded-xl border-2 h-12"
                    data-testid="2fa-password-input"
                  />
                  <Button
                    onClick={handleEnable2FA}
                    disabled={loading2FA}
                    className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12"
                    data-testid="enable-2fa-btn"
                  >
                    {loading2FA ? 'Setting up...' : 'Enable 2FA'}
                  </Button>
                </div>
              )}

              {show2FASetup && twoFASetupData && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-2xl border border-blue-200">
                  <h3 className="font-bold text-blue-900">Setup Two-Factor Authentication</h3>
                  
                  {/* QR Code placeholder - in production use actual QR */}
                  <div className="bg-white p-4 rounded-xl text-center">
                    <p className="text-sm text-gray-600 mb-2">Scan this with your authenticator app:</p>
                    <div className="bg-gray-100 p-4 rounded-lg inline-block">
                      <code className="text-xs break-all">{twoFASetupData.provisioning_uri}</code>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Or enter this secret manually:</p>
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <code className="font-mono text-sm bg-gray-100 px-3 py-1 rounded">{twoFASetupData.secret}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(twoFASetupData.secret)}
                        data-testid="copy-secret-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Backup codes */}
                  <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-yellow-800">Backup Codes</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowBackupCodes(!showBackupCodes)}
                        data-testid="toggle-backup-codes-btn"
                      >
                        {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-sm text-yellow-700 mb-2">Save these codes securely. They can be used if you lose access to your authenticator.</p>
                    {showBackupCodes && (
                      <div className="grid grid-cols-2 gap-2">
                        {twoFASetupData.backup_codes.map((code, idx) => (
                          <code key={idx} className="bg-white px-3 py-1 rounded text-sm font-mono">{code}</code>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Verification */}
                  <div className="space-y-2">
                    <Label className="font-semibold text-gray-700">Enter code from authenticator to verify</Label>
                    <Input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="000000"
                      className="rounded-xl border-2 h-12 text-center text-2xl tracking-widest"
                      maxLength={6}
                      data-testid="2fa-verify-input"
                    />
                    <Button
                      onClick={handleVerify2FA}
                      disabled={loading2FA}
                      className="w-full rounded-full bg-green-600 hover:bg-green-700 text-white font-bold h-12"
                      data-testid="verify-2fa-btn"
                    >
                      {loading2FA ? 'Verifying...' : 'Verify & Enable'}
                    </Button>
                  </div>
                </div>
              )}

              {twoFAStatus.enabled && (
                <div className="space-y-3 p-4 bg-red-50 rounded-2xl border border-red-200">
                  <h3 className="font-bold text-red-900">Disable Two-Factor Authentication</h3>
                  <Input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Your password"
                    className="rounded-xl border-2 h-12"
                    data-testid="disable-2fa-password"
                  />
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="2FA code or backup code"
                    className="rounded-xl border-2 h-12"
                    data-testid="disable-2fa-code"
                  />
                  <Button
                    onClick={handleDisable2FA}
                    disabled={loading2FA}
                    variant="destructive"
                    className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white font-bold h-12"
                    data-testid="disable-2fa-btn"
                  >
                    {loading2FA ? 'Disabling...' : 'Disable 2FA'}
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Privacy Settings Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-7 h-7 text-purple-500" />
              <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Privacy Settings</h2>
            </div>
            <PrivacySettings user={user} />
          </Card>

          {/* Digital Detox Section */}
          <Card className="p-8 rounded-3xl border border-green-200/50 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md mb-6" data-testid="detox-section">
            <div className="flex items-center gap-3 mb-6">
              <Leaf className="w-7 h-7 text-green-600" />
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Digital Detox</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Take a mindful break from social media</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Current Status */}
              <div className={`flex items-center justify-between p-4 rounded-2xl ${
                detoxStatus?.is_active 
                  ? 'bg-green-100 dark:bg-green-800/30 border border-green-300 dark:border-green-700' 
                  : 'bg-white dark:bg-gray-700'
              }`}>
                <div className="flex items-center gap-3">
                  {detoxStatus?.is_active ? (
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                      <Pause className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                      <Play className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {detoxStatus?.is_active ? 'Detox Active' : 'No Active Detox'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {detoxStatus?.is_active 
                        ? `${detoxStatus.time_remaining_formatted} remaining (${detoxStatus.progress_percentage}% complete)`
                        : 'Start a detox to take a break from notifications'
                      }
                    </p>
                  </div>
                </div>
                {detoxStatus?.is_active && (
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-100">
                    Active
                  </span>
                )}
              </div>

              {/* Active Detox Progress */}
              {detoxStatus?.is_active && (
                <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-green-200 dark:border-green-800">
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-300">Progress</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        {detoxStatus.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${detoxStatus.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-gray-500 dark:text-gray-400">Started</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(detoxStatus.started_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-gray-500 dark:text-gray-400">Ends</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {new Date(detoxStatus.scheduled_end).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {detoxStatus.auto_reply_message && (
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                      <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">Auto-reply message:</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{detoxStatus.auto_reply_message}</p>
                    </div>
                  )}

                  <Button
                    onClick={endDetox}
                    disabled={loadingDetox}
                    variant="outline"
                    className="w-full mt-4 rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid="end-detox-btn"
                  >
                    {loadingDetox ? 'Ending...' : 'End Detox Early'}
                  </Button>
                </div>
              )}

              {/* Start Detox Button */}
              {!detoxStatus?.is_active && !showDetoxSetup && (
                <Button
                  onClick={() => setShowDetoxSetup(true)}
                  className="w-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold h-12 shadow-lg"
                  data-testid="start-detox-btn"
                >
                  <Leaf className="w-4 h-4 mr-2" />
                  Start a Digital Detox
                </Button>
              )}

              {/* Detox Setup Form */}
              {showDetoxSetup && !detoxStatus?.is_active && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4 p-4 bg-white dark:bg-gray-800 rounded-2xl border border-green-200 dark:border-green-800"
                >
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-green-600" />
                    Configure Your Detox
                  </h3>

                  {/* Duration Presets */}
                  <div>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-3 block">How long do you want to detox?</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {detoxPresets.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => setSelectedPreset(preset.id)}
                          className={`p-3 rounded-xl border-2 transition-all text-center ${
                            selectedPreset === preset.id
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-green-300'
                          }`}
                          data-testid={`preset-${preset.id}`}
                        >
                          <span className="font-bold text-sm">{preset.label}</span>
                          <p className="text-xs mt-1 opacity-70">{preset.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reason (optional) */}
                  <div>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Why are you taking a break? (optional)</Label>
                    <Input
                      value={detoxReason}
                      onChange={(e) => setDetoxReason(e.target.value)}
                      placeholder="e.g., Need to focus on work, mental health break, vacation..."
                      className="rounded-xl border-2 h-12"
                      data-testid="detox-reason-input"
                    />
                  </div>

                  {/* Auto-reply Message */}
                  <div>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Auto-reply message for DMs</Label>
                    <Textarea
                      value={autoReply}
                      onChange={(e) => setAutoReply(e.target.value)}
                      className="rounded-xl border-2 resize-none"
                      rows={2}
                      data-testid="detox-auto-reply-input"
                    />
                  </div>

                  {/* Hide from Search Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Hide from search</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Others won't find you while on detox</p>
                    </div>
                    <button
                      onClick={() => setHideFromSearch(!hideFromSearch)}
                      className={`w-14 h-8 rounded-full transition-all ${
                        hideFromSearch ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      data-testid="hide-from-search-toggle"
                    >
                      <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        hideFromSearch ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Info Box */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>What happens during detox:</strong>
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                      <li>• All notifications will be paused</li>
                      <li>• Your auto-reply will be sent to anyone who DMs you</li>
                      <li>• You can end your detox early anytime by logging back in</li>
                      <li>• Your posts and profile remain visible unless you hide from search</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowDetoxSetup(false)}
                      variant="outline"
                      className="flex-1 rounded-full h-12"
                      data-testid="cancel-detox-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={startDetox}
                      disabled={loadingDetox}
                      className="flex-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold h-12"
                      data-testid="confirm-start-detox-btn"
                    >
                      {loadingDetox ? 'Starting...' : 'Start Detox 🌿'}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Detox Stats */}
              {detoxStats && detoxStats.total_detoxes > 0 && (
                <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Your Detox Journey</h4>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-2xl font-bold text-green-600">{detoxStats.total_detoxes}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Detoxes</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-2xl font-bold text-green-600">{detoxStats.total_hours}h</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Hours Detoxed</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-2xl font-bold text-green-600">{detoxStats.average_completion_rate}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Avg Completion</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <p className="text-2xl font-bold text-green-600">{detoxStats.streak || 0}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Week Streak</p>
                    </div>
                  </div>
                </div>
              )}

              {/* View History Link */}
              <button
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API_URL}/detox/history?limit=5`, { withCredentials: true });
                    const history = response.data.history || [];
                    if (history.length === 0) {
                      toast.info("You haven't done any detoxes yet. Start your first one today!");
                    } else {
                      const historyText = history.map((d, i) => 
                        `${i + 1}. ${new Date(d.started_at).toLocaleDateString()} - ${d.duration_hours}h (${d.status})`
                      ).join('\n');
                      toast.info(`Recent Detoxes:\n${historyText}`, { duration: 8000 });
                    }
                  } catch (error) {
                    toast.error('Failed to load history');
                  }
                }}
                className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline"
                data-testid="view-detox-history-btn"
              >
                <History className="w-4 h-4" />
                View Detox History
              </button>
            </div>
          </Card>

          {/* Verification Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-7 h-7 text-blue-500" />
              <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Account Verification</h2>
            </div>
            
            <div className="space-y-4">
              {/* Current Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl">
                <div className="flex items-center gap-3">
                  {user?.is_verified ? (
                    <CheckCircle2 className="w-6 h-6 text-blue-500" />
                  ) : verificationStatus?.verification_request?.status === 'pending' ? (
                    <Clock className="w-6 h-6 text-yellow-500" />
                  ) : verificationStatus?.verification_request?.status === 'under_review' ? (
                    <Clock className="w-6 h-6 text-blue-500" />
                  ) : verificationStatus?.verification_request?.status === 'rejected' ? (
                    <XCircle className="w-6 h-6 text-red-500" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Verification Status</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {user?.is_verified 
                        ? 'Your account is verified with a blue checkmark'
                        : verificationStatus?.verification_request?.status === 'pending'
                          ? 'Your request is pending review'
                          : verificationStatus?.verification_request?.status === 'under_review'
                            ? 'Your request is being reviewed'
                            : verificationStatus?.verification_request?.status === 'rejected'
                              ? 'Your request was rejected'
                              : 'Get a verified badge to prove your identity'
                      }
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  user?.is_verified 
                    ? 'bg-blue-100 text-blue-700'
                    : verificationStatus?.verification_request?.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : verificationStatus?.verification_request?.status === 'under_review'
                        ? 'bg-blue-100 text-blue-700'
                        : verificationStatus?.verification_request?.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-200 text-gray-600'
                }`}>
                  {user?.is_verified 
                    ? 'Verified'
                    : verificationStatus?.verification_request?.status === 'pending'
                      ? 'Pending'
                      : verificationStatus?.verification_request?.status === 'under_review'
                        ? 'Under Review'
                        : verificationStatus?.verification_request?.status === 'rejected'
                          ? 'Rejected'
                          : 'Not Verified'
                  }
                </span>
              </div>

              {/* Rejection reason */}
              {verificationStatus?.verification_request?.status === 'rejected' && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800">
                  <p className="font-semibold text-red-800 dark:text-red-200 mb-1">Rejection Reason:</p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {verificationStatus.verification_request.rejection_reason}
                  </p>
                </div>
              )}

              {/* Show form button or form */}
              {!user?.is_verified && !verificationStatus?.verification_request?.status?.match(/pending|under_review/) && !showVerificationForm && (
                <Button
                  onClick={() => setShowVerificationForm(true)}
                  className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                  data-testid="start-verification-btn"
                >
                  <BadgeCheck className="w-4 h-4 mr-2" />
                  Apply for Verification
                </Button>
              )}

              {/* Verification Form */}
              {showVerificationForm && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800"
                >
                  <h3 className="font-bold text-blue-900 dark:text-blue-100">Request Verification</h3>
                  
                  {/* Type Selection */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setVerificationType('individual')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        verificationType === 'individual'
                          ? 'border-blue-500 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}
                      data-testid="verification-type-individual"
                    >
                      <User className="w-6 h-6" />
                      <span className="font-semibold text-sm">Individual</span>
                    </button>
                    <button
                      onClick={() => setVerificationType('company')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        verificationType === 'company'
                          ? 'border-blue-500 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      }`}
                      data-testid="verification-type-company"
                    >
                      <Building2 className="w-6 h-6" />
                      <span className="font-semibold text-sm">Company</span>
                    </button>
                  </div>

                  {/* Common Fields */}
                  <div>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">
                      {verificationType === 'individual' ? 'Full Legal Name' : 'Your Full Name'}
                    </Label>
                    <Input
                      value={verificationForm.full_name}
                      onChange={(e) => setVerificationForm({ ...verificationForm, full_name: e.target.value })}
                      placeholder="As shown on your ID"
                      className="rounded-xl border-2 h-12"
                      data-testid="verification-full-name"
                    />
                  </div>

                  {/* Individual Fields */}
                  {verificationType === 'individual' && (
                    <div>
                      <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">ID Type</Label>
                      <select
                        value={verificationForm.id_type}
                        onChange={(e) => setVerificationForm({ ...verificationForm, id_type: e.target.value })}
                        className="w-full h-12 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4"
                        data-testid="verification-id-type"
                      >
                        <option value="passport">Passport</option>
                        <option value="drivers_license">Driver's License</option>
                        <option value="national_id">National ID Card</option>
                      </select>
                    </div>
                  )}

                  {/* Company Fields */}
                  {verificationType === 'company' && (
                    <>
                      <div>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Company Name</Label>
                        <Input
                          value={verificationForm.company_name}
                          onChange={(e) => setVerificationForm({ ...verificationForm, company_name: e.target.value })}
                          placeholder="Official company name"
                          className="rounded-xl border-2 h-12"
                          data-testid="verification-company-name"
                        />
                      </div>
                      <div>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Your Role</Label>
                        <Input
                          value={verificationForm.company_role}
                          onChange={(e) => setVerificationForm({ ...verificationForm, company_role: e.target.value })}
                          placeholder="e.g., CEO, Marketing Director, Spokesperson"
                          className="rounded-xl border-2 h-12"
                          data-testid="verification-company-role"
                        />
                      </div>
                      <div>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Company Website</Label>
                        <Input
                          value={verificationForm.company_website}
                          onChange={(e) => setVerificationForm({ ...verificationForm, company_website: e.target.value })}
                          placeholder="https://company.com"
                          className="rounded-xl border-2 h-12"
                          data-testid="verification-company-website"
                        />
                      </div>
                      <div>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Official Company Email</Label>
                        <Input
                          value={verificationForm.company_email}
                          onChange={(e) => setVerificationForm({ ...verificationForm, company_email: e.target.value })}
                          placeholder="you@company.com"
                          className="rounded-xl border-2 h-12"
                          data-testid="verification-company-email"
                        />
                      </div>
                    </>
                  )}

                  {/* Additional Info */}
                  <div>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200 mb-2 block">Additional Information</Label>
                    <Textarea
                      value={verificationForm.additional_info}
                      onChange={(e) => setVerificationForm({ ...verificationForm, additional_info: e.target.value })}
                      placeholder="Any additional details to support your request..."
                      className="rounded-xl border-2 resize-none"
                      rows={3}
                      data-testid="verification-additional-info"
                    />
                  </div>

                  {/* Note about documents */}
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                      <div className="text-sm text-yellow-800 dark:text-yellow-200">
                        <p className="font-semibold">Document Requirements:</p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          {verificationType === 'individual' ? (
                            <>
                              <li>Clear photo of your government-issued ID</li>
                              <li>ID must match the name on your account</li>
                            </>
                          ) : (
                            <>
                              <li>Business registration documents</li>
                              <li>Proof of your role (e.g., company letterhead)</li>
                              <li>Verification email will be sent to company domain</li>
                            </>
                          )}
                        </ul>
                        <p className="mt-2 text-xs">Our team will contact you if additional documents are needed.</p>
                      </div>
                    </div>
                  </div>

                  {/* Submit buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowVerificationForm(false)}
                      variant="outline"
                      className="flex-1 rounded-full h-12"
                      data-testid="cancel-verification-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={submitVerificationRequest}
                      disabled={submittingVerification}
                      className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                      data-testid="submit-verification-btn"
                    >
                      {submittingVerification ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </Card>

          {/* Liveness Verification Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Camera className="w-7 h-7 text-purple-500" />
              <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Liveness Verification</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-2xl">
                <div className="flex items-center gap-3">
                  {user?.liveness_verified ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Selfie Verification</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {user?.liveness_verified 
                        ? 'Your identity has been verified via selfie'
                        : 'Verify you\'re a real person with a quick selfie check'
                      }
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  user?.liveness_verified 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {user?.liveness_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>

              {!user?.liveness_verified && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-200 dark:border-purple-800">
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400 mt-0.5" />
                    <div>
                      <p className="font-semibold text-purple-800 dark:text-purple-200 mb-1">Why verify?</p>
                      <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1 list-disc ml-4">
                        <li>Increases your trust score</li>
                        <li>Required for certain premium features</li>
                        <li>Helps protect the community from bots</li>
                        <li>Takes less than 2 minutes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {!user?.liveness_verified && (
                <Button
                  onClick={() => window.location.href = '/verify/liveness'}
                  className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                  data-testid="start-liveness-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Start Liveness Verification
                </Button>
              )}

              {user?.liveness_verified && user?.liveness_verified_at && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Verified on {new Date(user.liveness_verified_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </Card>

          {/* Appearance / Theme Section */}
          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              {isDark ? <Moon className="w-7 h-7 text-primary" /> : <Sun className="w-7 h-7 text-primary" />}
              <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Appearance</h2>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">Choose your preferred theme</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => !isDark || toggleTheme()}
                  className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                    !isDark 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                  }`}
                  data-testid="theme-light-btn"
                >
                  <Sun className="w-8 h-8" />
                  <span className="font-semibold">Light</span>
                </button>
                <button
                  onClick={() => isDark || toggleTheme()}
                  className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                    isDark 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="theme-dark-btn"
                >
                  <Moon className="w-8 h-8" />
                  <span className="font-semibold">Dark</span>
                </button>
              </div>
            </div>
          </Card>

          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md mb-6">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Account</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div>
                  <p className="font-semibold text-gray-900">Subscription</p>
                  <p className="text-sm text-gray-600 capitalize">{user?.subscription_tier} Plan</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md mb-6">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Legal</h2>
            <div className="space-y-3">
              <Link to="/privacy" className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-gray-900">Privacy Policy</span>
                <span className="text-sm text-gray-500">View →</span>
              </Link>
              <Link to="/terms" className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-gray-900">Terms of Service</span>
                <span className="text-sm text-gray-500">View →</span>
              </Link>
              <Link to="/conduct" className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                <span className="font-semibold text-gray-900">Code of Conduct</span>
                <span className="text-sm text-gray-500">View →</span>
              </Link>
            </div>
          </Card>

          {/* GDPR Data Rights Section */}
          <Card className="p-8 rounded-3xl border border-blue-200 shadow-xl bg-blue-50/70 dark:bg-blue-900/20 backdrop-blur-md mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Database className="w-7 h-7 text-blue-600" />
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">Your Data Rights (GDPR)</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage your personal data under EU regulations</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Data Export */}
              <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Download Your Data</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Request a copy of all your personal data including posts, comments, likes, and account information.
                    </p>
                    <Button
                      onClick={async () => {
                        try {
                          toast.loading('Preparing your data export...');
                          const response = await axios.get(`${API_URL}/gdpr/export`, { 
                            withCredentials: true,
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `socyy-data-export-${new Date().toISOString().split('T')[0]}.json`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.dismiss();
                          toast.success('Your data has been downloaded!');
                        } catch (error) {
                          toast.dismiss();
                          toast.error('Failed to export data. Please try again.');
                        }
                      }}
                      variant="outline"
                      className="rounded-full font-semibold"
                      data-testid="gdpr-export-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export My Data
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cookie Preferences */}
              <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Cookie Preferences</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Manage your cookie consent settings at any time.
                    </p>
                    <Button
                      onClick={() => {
                        localStorage.removeItem('cookieConsent');
                        window.location.reload();
                      }}
                      variant="outline"
                      className="rounded-full font-semibold"
                      data-testid="gdpr-cookie-reset-btn"
                    >
                      Reset Cookie Preferences
                    </Button>
                  </div>
                </div>
              </div>

              {/* Data Deletion Info */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-semibold mb-1">Right to Erasure ("Right to be Forgotten")</p>
                    <p>
                      You can request complete deletion of your account and all associated data. 
                      This action is permanent and cannot be undone. Use the "Delete Account" option in the Danger Zone below.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-8 rounded-3xl border border-red-200 shadow-xl bg-red-50/70 backdrop-blur-md">
            <h2 className="text-2xl font-heading font-bold text-red-900 mb-4">Danger Zone</h2>
            <div className="space-y-4">
              <Button
                onClick={handleLogout}
                variant="destructive"
                className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white font-bold h-12 shadow-lg"
                data-testid="settings-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
              
              <div className="border-t border-red-200 pt-4">
                <h3 className="font-semibold text-red-800 mb-2">Delete Account</h3>
                <p className="text-sm text-red-700 mb-3">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button
                  onClick={async () => {
                    const confirmed = window.confirm(
                      'Are you absolutely sure you want to delete your account?\n\n' +
                      'This will permanently delete:\n' +
                      '• Your profile and all personal information\n' +
                      '• All your posts, comments, and likes\n' +
                      '• Your follower/following relationships\n' +
                      '• All uploaded media\n\n' +
                      'This action CANNOT be undone.'
                    );
                    if (confirmed) {
                      const doubleConfirm = window.prompt(
                        'To confirm deletion, please type "DELETE" (case sensitive):'
                      );
                      if (doubleConfirm === 'DELETE') {
                        try {
                          await axios.delete(`${API_URL}/gdpr/delete-account`, { withCredentials: true });
                          toast.success('Your account has been deleted. Goodbye!');
                          navigate('/');
                        } catch (error) {
                          toast.error('Failed to delete account. Please contact support.');
                        }
                      } else {
                        toast.error('Account deletion cancelled - confirmation text did not match.');
                      }
                    }
                  }}
                  variant="outline"
                  className="w-full rounded-full border-red-300 text-red-700 hover:bg-red-100 font-bold h-12"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Settings;
